import {Component, OnInit, ViewChild} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {PasskeyService} from '../../_services';
import {environment} from '../../../environments/environment';

/**
 * JS-1396 — "set up a passkey on this device", reached from passkey-verify.
 *
 * A passkey lives on the device (or password manager) that created it, so a
 * user who already has 2FA had no way in on a phone or a second laptop except
 * by spending a recovery code. Here the password step's login_token buys an
 * emailed 6-digit code; the code buys an enrolment_token; passkey-enrol then
 * creates the passkey on this device and completes the login. Existing
 * passkeys are untouched and no recovery code is used.
 *
 * Every call goes through PasskeyService's rawHttp, so `error` is a genuine
 * HttpErrorResponse with a trustworthy `.status`.
 */
@Component({
  moduleId: module.id,
  selector: 'app-passkey-device-code',
  templateUrl: `../views/passkey-device-code.component.html`,
  styleUrls: ['../styles/auth-style.scss'],
  providers: [MessageService]
})
export class PasskeyDeviceCodeComponent implements OnInit {
  @ViewChild('codeInput') codeInput: any;

  public logoUrl = environment.logoUrl;
  public loginToken: string = null;
  public deviceToken: string = null;
  public code = '';
  public sending = false;
  public verifying = false;
  public incorrectCode = false;

  constructor(
    private router: Router,
    private passkeyService: PasskeyService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation && navigation.extras && navigation.extras.state;
    if (state) {
      this.loginToken = (state as any).loginToken;
    }
  }

  ngOnInit() {
    // Reached directly, by refresh or a pasted URL, there is no token and no
    // way to get one here. Send them back rather than showing a dead screen.
    if (!this.loginToken) {
      this.router.navigate(['/login']);
      return;
    }
    this.sendCode();
  }

  /**
   * First send uses the login_token; any resend uses the device_token the
   * previous send returned, which outlives the login_token's short TTL.
   */
  sendCode(isResend = false) {
    if (this.sending) {
      return;
    }
    this.sending = true;
    this.incorrectCode = false;

    const tokens = this.deviceToken ? {deviceToken: this.deviceToken} : {loginToken: this.loginToken};

    this.passkeyService.deviceCode(tokens).subscribe(
      response => {
        this.sending = false;
        this.deviceToken = response.device_token;
        if (isResend) {
          this.resetCodeInput();
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('Success!'),
            detail: this.translate.instant('code sent sucessfully')
          });
        }
      },
      (error: HttpErrorResponse) => {
        this.sending = false;
        this.handleError(error);
      }
    );
  }

  onCodeChanged(code: string) {
    if (code.length < 6) {
      this.incorrectCode = false;
    }
    this.code = code;
  }

  onCodeCompleted(code: string) {
    this.code = code;
    this.verify();
  }

  verify() {
    // codeCompleted and the button can both fire for one entry; each request
    // spends one of the code's five attempts.
    if (this.verifying || !this.deviceToken || this.code.length < 6) {
      return;
    }
    this.verifying = true;
    this.incorrectCode = false;

    this.passkeyService.deviceVerify(this.deviceToken, this.code).subscribe(
      response => {
        this.verifying = false;
        this.router.navigate(['/passkey-enrol'], {
          state: {enrolmentToken: response.enrolment_token, addDevice: true}
        });
      },
      (error: HttpErrorResponse) => {
        this.verifying = false;
        // A wrong code and a dead device_token are both 403; only the
        // message tells them apart, and only a wrong code is worth retrying.
        const message = error && error.error && error.error.error && error.error.error.message;
        if (error && error.status === 403 && message === 'That code is not valid.') {
          this.incorrectCode = true;
          this.resetCodeInput();
          return;
        }
        this.handleError(error);
      }
    );
  }

  backToLogin() {
    this.router.navigate(['/login']);
  }

  private handleError(error: HttpErrorResponse) {
    if (error && error.status === 429) {
      // Same split as passkey-recovery: the api's own exhausted-budget 429
      // carries a code and means start over; the framework throttle's 429
      // carries none and only means wait.
      const body = error.error;
      const code = body && body.error && body.error.code;
      if (code === 'device_code_attempts_exhausted') {
        this.returnToLogin('too many attempts please log in again');
        return;
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Error!'),
        detail: this.translate.instant('too many requests please wait a moment')
      });
      return;
    }

    if (error && error.status === 403) {
      this.returnToLogin('This sign-in attempt has expired. Please log in again.');
      return;
    }

    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('Error!'),
      detail: this.translate.instant('something went wrong')
    });
  }

  private returnToLogin(messageTranslationKey: string) {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('Error!'),
      detail: this.translate.instant(messageTranslationKey)
    });
    setTimeout(() => this.router.navigate(['/login']), 2500);
  }

  private resetCodeInput() {
    this.code = '';
    if (this.codeInput) {
      this.codeInput.reset();
    }
  }
}
